import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { stageMediaObject } from '../../../chat/upload'
import { FeatherIcon } from '../../../components/FeatherIcon'
import { Spinner } from '../../../components/Spinner'
import { useMediaSrc } from '../../../library/useMediaSrc'
import { Button, ConfirmModal, Empty, ErrorLine, Field, Loading, Modal } from '../../settings/kit'
import { useLoad } from '../../settings/kitHooks'
import { errorText, type StreamingCtx } from './context'
import { ScheduledEvents, SubscriptionCard } from './Sidebar'
import {
  CREATE_REFERER,
  DESCRIPTION_MAX,
  DESCRIPTION_MIN,
  dateProblems,
  defaultSchedule,
  editorHtml,
  editReferer,
  joinLocal,
  localIso,
  parseScheduledStream,
  plainText,
  splitLocal,
  streamBody,
  type CoverMedia,
  type ScheduledStream,
  type StreamingView,
} from './streamingData'

/** Photos only, no GIFs (OnlyFans' cover uploader). */
const COVER_ACCEPT = 'image/jpeg,image/png,image/webp,image/heic,image/heif'

/**
 * NEW STREAM / EDIT LIVE STREAM (OnlyFans' CreateEditScheduleStream): date and
 * time, an optional cover, the stream info, SCHEDULE (or SAVE when editing).
 */
export function CreateEditStream({ ctx, view }: { ctx: StreamingCtx; view: Exclude<StreamingView, { kind: 'dashboard' }> }) {
  const { t } = useTranslation('ofMore', { keyPrefix: 'streamingPage' })
  const editId = view.kind === 'edit' ? view.id : null
  const referer = editId ? editReferer(editId) : CREATE_REFERER
  const initial = useLoad(async (): Promise<ScheduledStream | null> => {
    if (!editId) return null
    return parseScheduledStream(await ctx.of.get(`streams/${editId}`, {}, referer))
  }, [ctx.of, editId])

  const back = () => ctx.go('', true)

  let main
  if (!ctx.me.isScheduledStreamsAllowed) {
    main = <Empty>{t('notAllowed')}</Empty>
  } else if (editId && initial.loading) {
    main = <Loading />
  } else if (editId && !initial.data) {
    main = initial.error ? <ErrorLine onRetry={initial.reload}>{t('loadFailed', { error: initial.error })}</ErrorLine> : <Empty>{t('notFound')}</Empty>
  } else {
    main = null
  }

  return (
    <div className="ofst-columns">
      <div className="ofst-main">
        {main === null ? (
          <StreamForm ctx={ctx} view={view} initial={initial.data ?? null} referer={referer} />
        ) : (
          <div className="oset-section">
            <header className="oset-head">
              <button type="button" className="oset-head-btn" aria-label={t('back')} onClick={back}>
                <FeatherIcon name="arrow-left" size={24} />
              </button>
              <h1 className="oset-title">{editId ? t('editStream') : t('newStream')}</h1>
            </header>
            <div className="oset-body">{main}</div>
          </div>
        )}
      </div>
      <aside className="ofst-side">
        <SubscriptionCard ctx={ctx} />
        <ScheduledEvents ctx={ctx} referer={referer} editingId={editId ?? undefined} />
      </aside>
    </div>
  )
}

function StreamForm({ ctx, view, initial, referer }: { ctx: StreamingCtx; view: Exclude<StreamingView, { kind: 'dashboard' }>; initial: ScheduledStream | null; referer: string }) {
  const { t } = useTranslation('ofMore', { keyPrefix: 'streamingPage' })
  const start = initial ? initial.scheduledAt : defaultSchedule(view.kind === 'create' ? view.scheduleDate : '')
  const startParts = splitLocal(start)
  const startText = initial ? initial.rawDescription || plainText(initial.description) : ''
  const [date, setDate] = useState(startParts.date)
  const [time, setTime] = useState(startParts.time)
  const [text, setText] = useState(startText)
  const [preview, setPreview] = useState(initial?.preview ?? '')
  const [cover, setCover] = useState<CoverMedia | null>(null)
  const [uploading, setUploading] = useState(false)
  const [showErrors, setShowErrors] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [asking, setAsking] = useState<'consent' | 'leave' | null>(null)
  const fileRef = useRef<HTMLInputElement | null>(null)
  const blobRef = useRef('')
  useEffect(() => () => void (blobRef.current && URL.revokeObjectURL(blobRef.current)), [])

  const at = joinLocal(date, time)
  // The inputs hold whole minutes, so compare at that precision.
  const startMs = Math.floor(new Date(start).getTime() / 60_000) * 60_000
  const problems = dateProblems(at)
  const descLength = text.trim().length
  const errors = {
    date: !date ? t('required', { field: t('date') }) : problems.date ? t('notValid', { field: t('date') }) : '',
    time: !time ? t('required', { field: t('time') }) : problems.time ? t('notValid', { field: t('time') }) : '',
    description: !descLength
      ? t('required', { field: t('descriptionField') })
      : descLength < DESCRIPTION_MIN
        ? t('minLength', { field: t('descriptionField'), length: DESCRIPTION_MIN })
        : '',
  }
  const invalid = Boolean(errors.date || errors.time || errors.description || text.length > DESCRIPTION_MAX)
  const changed = initial ? text !== startText || (at?.getTime() ?? NaN) !== startMs || preview !== initial.preview : Boolean(preview || text)
  const submitDisabled = !ctx.canWrite || saving || uploading || (initial ? !changed : false) || (showErrors && invalid)

  const leave = () => (changed && !saving ? setAsking('leave') : ctx.go('', true))

  const pickCover = async (file: File | undefined) => {
    if (fileRef.current) fileRef.current.value = ''
    if (!file) return
    if (blobRef.current) URL.revokeObjectURL(blobRef.current)
    const local = URL.createObjectURL(file)
    blobRef.current = local
    setPreview(local)
    setUploading(true)
    setError('')
    try {
      setCover(await stageMediaObject(ctx.accountId, '', file))
    } catch (e) {
      setPreview(initial?.preview ?? '')
      setCover(null)
      setError(errorText(e, t('error')))
    } finally {
      setUploading(false)
    }
  }

  const removeCover = () => {
    setPreview('')
    setCover(null)
  }

  const save = async (consent: boolean) => {
    setShowErrors(true)
    if (invalid || !at || !ctx.me.canUploadMedia) return
    setSaving(true)
    setError('')
    const body = streamBody(
      { description: editorHtml(text), scheduledDate: localIso(at), cover, hasPreview: Boolean(preview), consent },
      initial ? { hadMedia: initial.hasMedia } : null,
    )
    try {
      if (initial) await ctx.of.send('PATCH', `streams/${initial.id}`, referer, body)
      else await ctx.of.send('POST', 'streams', referer, body)
      // OnlyFans then opens the Queue on the stream's day.
      ctx.openPage('queue')
    } catch (e) {
      setError(errorText(e, t('error')))
      setSaving(false)
    }
  }

  const onSchedule = () => {
    // ensureConsentAccepted comes first, as on OnlyFans.
    if (ctx.me.needConsent) setAsking('consent')
    else void save(false)
  }

  return (
    <div className="oset-section">
      <header className="oset-head">
        <button type="button" className="oset-head-btn" aria-label={t('back')} onClick={leave}>
          <FeatherIcon name="arrow-left" size={24} />
        </button>
        <h1 className="oset-title">{initial ? t('editStream') : t('newStream')}</h1>
        {ctx.me.canUploadMedia && (
          <div className="oset-head-actions">
            <Button busy={saving} disabled={submitDisabled} onClick={onSchedule}>
              {initial ? t('save') : t('schedule')}
            </Button>
          </div>
        )}
      </header>
      <div className="oset-body ofst-form">
        <div className="ofst-when">
          <Field label={t('date')} type="date" value={date} onChange={setDate} disabled={!ctx.canWrite} error={showErrors ? errors.date : undefined} />
          <Field label={t('time')} type="time" value={time} onChange={setTime} disabled={!ctx.canWrite} error={showErrors ? errors.time : undefined} />
        </div>
        <Cover preview={preview} uploading={uploading} disabled={!ctx.canWrite} onAdd={() => fileRef.current?.click()} onRemove={removeCover} />
        <input ref={fileRef} type="file" accept={COVER_ACCEPT} hidden onChange={(e) => void pickCover(e.target.files?.[0])} />
        <div className="ofst-desc">
          <Field
            label={t('streamInfo')}
            value={text}
            onChange={setText}
            multiline
            rows={6}
            maxLength={DESCRIPTION_MAX}
            disabled={!ctx.canWrite}
            error={showErrors ? errors.description : undefined}
          />
        </div>
        {error && <p className="oset-field-error ofst-form-error">{error}</p>}
      </div>
      {asking === 'consent' && (
        <Modal
          onClose={() => setAsking(null)}
          actions={
            <>
              <Button variant="flat" onClick={() => setAsking(null)}>
                {t('cancel')}
              </Button>
              <Button
                variant="flat"
                onClick={() => {
                  setAsking(null)
                  void save(true)
                }}
              >
                {t('yes')}
              </Button>
            </>
          }
        >
          <p className="oset-modal-text ofst-prewrap">{t('consent')}</p>
        </Modal>
      )}
      {asking === 'leave' && <ConfirmModal text={t('leave')} confirm={t('yes')} onClose={() => setAsking(null)} onConfirm={() => ctx.go('', true)} />}
    </div>
  )
}

/** The cover area: OnlyFans' placeholder (or the cover) with ADD COVER / REMOVE COVER over it. */
function Cover({ preview, uploading, disabled, onAdd, onRemove }: { preview: string; uploading: boolean; disabled: boolean; onAdd: () => void; onRemove: () => void }) {
  const { t } = useTranslation('ofMore', { keyPrefix: 'streamingPage' })
  const { ref, src } = useMediaSrc(preview)
  return (
    <div className={`ofst-cover ${preview ? 'has-cover' : ''}`} ref={ref as React.RefObject<HTMLDivElement>}>
      {preview && src && (
        <>
          <img className="ofst-cover-bg" src={src} alt="" aria-hidden="true" />
          <img className="ofst-cover-img" src={src} alt="" />
        </>
      )}
      {uploading && (
        <span className="ofst-cover-busy" aria-label={t('uploading')}>
          <Spinner size={32} />
        </span>
      )}
      <div className="ofst-cover-controls">
        {preview ? (
          <button type="button" className="ofst-cover-btn" disabled={disabled || uploading} onClick={onRemove}>
            <FeatherIcon name="trash-2" size={18} />
            <span>{t('removeCover')}</span>
          </button>
        ) : (
          <button type="button" className="ofst-cover-btn" disabled={disabled || uploading} onClick={onAdd}>
            <FeatherIcon name="image" size={18} />
            <span>{t('addCover')}</span>
          </button>
        )}
      </div>
    </div>
  )
}
